import { EVENTS, subscribe } from '@utilities/events';
import { parseConfig } from '@utilities/theme';

function asBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
}

function positiveInt(value) {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function variantMapKey(variant) {
  if (!variant || variant.title == null) {
    return '';
  }
  return String(variant.title).trim();
}

function findVariantPairBySideId(config, variantId, sideKey) {
  const map = config?.variantMap;
  if (!variantId || !map || typeof map !== 'object') {
    return null;
  }

  for (const pair of Object.values(map)) {
    if (positiveInt(pair?.[sideKey]?.id) === variantId) {
      return pair;
    }
  }

  return null;
}

/**
 * Resolve the original ↔ monogram variant pair for the current PDP selection.
 * Title match first; then id match using landed product context.
 */
function resolveVariantPair(config, variant) {
  if (!variant) {
    return null;
  }

  const key = variantMapKey(variant);
  const map = config?.variantMap;
  if (key && map && typeof map === 'object' && map[key]) {
    return map[key];
  }

  const variantId = positiveInt(variant.id);
  if (!variantId) {
    return null;
  }

  if (config.landedOnMonogrammedProduct) {
    const byMonogram = findVariantPairBySideId(config, variantId, 'monogrammedProductVariant');
    if (byMonogram) {
      return byMonogram;
    }
  }

  if (config.landedOnOriginalProduct) {
    const byOriginal = findVariantPairBySideId(config, variantId, 'originalProductVariant');
    if (byOriginal) {
      return byOriginal;
    }
  }

  return (
    findVariantPairBySideId(config, variantId, 'monogrammedProductVariant')
    || findVariantPairBySideId(config, variantId, 'originalProductVariant')
  );
}

/**
 * Monogram UI + ATC resolution for the current selection.
 * Monogramming is offered only when the mapped monogram variant is greyson-available.
 * ATC sends the monogram variant when committed and available; otherwise the original variant.
 */
function resolveMonogramSelection(config, variant, { committed = false } = {}) {
  const pair = resolveVariantPair(config, variant);
  const originalVariantId = positiveInt(pair?.originalProductVariant?.id);
  const monogramVariantId = positiveInt(pair?.monogrammedProductVariant?.id);
  const monogramAvailable = Boolean(
    pair?.monogrammedProductVariant && asBoolean(pair.monogrammedProductVariant.available)
  );
  const sendMonogramVariant = Boolean(committed && monogramAvailable && monogramVariantId);
  const addToCartVariantId = sendMonogramVariant ? monogramVariantId : originalVariantId;

  return {
    pair,
    originalVariantId,
    monogramVariantId,
    monogramAvailable,
    sendMonogramVariant,
    addToCartVariantId,
    landedOnOriginalProduct: config.landedOnOriginalProduct === true,
    landedOnMonogrammedProduct: config.landedOnMonogrammedProduct === true,
  };
}

function buildMonogramAddToCartPayload(config, variant, state) {
  const selection = resolveMonogramSelection(config, variant, { committed: state.committed });
  const { addToCartVariantId, sendMonogramVariant } = selection;

  if (!addToCartVariantId) {
    return { selection, payload: null };
  }

  if (sendMonogramVariant) {
    const amount = Number(config.monogramPriceAmount);
    return {
      selection,
      payload: {
        id: addToCartVariantId,
        properties: {
          "Monogram": state.committedText,
          "_monogramFont": state.committedFont,
          "Monogram Color": state.committedColorLabel,
          "_monogramPrice": Number.isFinite(amount) ? amount : 0,
        },
      },
    };
  }

  return {
    selection,
    payload: { id: addToCartVariantId },
  };
}

/** @returns {string} Shopify CDN URL or empty string when no mockup for this variant. */
function mockupMediaForVariant(config, variant) {
  const raw = resolveVariantPair(config, variant)?.monogrammedProductVariant?.mockupMedia;
  return typeof raw === 'string' ? raw.trim() : '';
}

/** ASCII A–Z only (excludes digits, spaces, accents, symbols). */
const MONOGRAM_TEXT_INVALID = /[^A-Za-z]/g;

function normalizeInput(raw, maxLen) {
  return String(raw ?? '')
    .replace(MONOGRAM_TEXT_INVALID, '')
    .slice(0, maxLen);
}

/** @returns {string} CSS font-family stack for a monogram font name. */
function monogramFontFamilyCss(font) {
  const name = String(font || '').trim();
  if (!name) {
    return 'inherit';
  }
  return `'${name}', 'Times New Roman', serif`;
}

/** @returns {string} Preview overlay HTML (A–Z only; center char wrapped at length 3). */
function buildMonogramPreviewHtml(text) {
  const value = String(text || '').trim();
  if (value.length === 3) {
    return `${value.charAt(0)}<span class="center-monogram">${value.charAt(1)}</span>${value.charAt(2)}`;
  }
  return value;
}

/** Liquid sets these on `<pdp-monogramming>` (physical height in inches × mockup_scale). */
const MONOGRAM_PREVIEW_SIZE_VARS = {
  single: '--pdp-monogramming-preview-single-character-text-size',
  two: '--pdp-monogramming-preview-two-character-text-size',
  threeOuter: '--pdp-monogramming-preview-three-character-outer-text-size',
  threeInner: '--pdp-monogramming-preview-three-character-inner-text-size',
  overlay: '--pdp-monogramming-preview-text-size',
  center: '--pdp-monogramming-preview-center-text-size',
};

/** @param {unknown} raw — x/y are % from top-left of preview wrap (default 50 = center). */
function normalizeMockupPlacement(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const x = Number(src.x);
  const y = Number(src.y);
  const angle = Number(src.angle);

  return {
    x: Number.isFinite(x) ? x : 50,
    y: Number.isFinite(y) ? y : 50,
    angle: Number.isFinite(angle) ? angle : 0,
  };
}

/**
 * @classdesc PDP monogram UI: modal, preview, variant sync. Phase 1 only (no cart bundle).
 * @extends HTMLElement
 */
class PdpMonogramming extends HTMLElement {
  /** @type {(() => void)[]} */
  _variantUnsubscribers = [];

  /** @type {HTMLElement | null} */
  _productOverview = null;
  _config = {};
  /** @type {HTMLElement | null} */
  _dialogPreviousFocus = null;
  _uiReady = false;

  _state = {
    committed: false,
    committedText: '',
    committedFont: '',
    committedColorHandle: '',
    committedColorLabel: '',
    committedColorFamily: '',
    selection: null,
    currentMonogramVariantId: null,
    monogramAddToCartData: null,
    draftText: '',
    draftFont: '',
    draftColorHandle: '',
    draftColorLabel: '',
    draftColorFamily: '',
  };

  /** @type {HTMLDialogElement | null} */
  _dialog = null;
  /** @type {HTMLButtonElement | null} */
  _btnInitial = null;
  /** @type {HTMLElement | null} */
  _rowAdded = null;
  /** @type {HTMLElement | null} */
  _triggerLabel = null;
  /** @type {HTMLElement | null} */
  _orderNotice = null;
  /** @type {HTMLTemplateElement | null} */
  _orderNoticeTemplate = null;
  /** @type {HTMLElement | null} */
  _mockupWrap = null;
  /** @type {HTMLImageElement | null} */
  _mockupImg = null;
  /** @type {HTMLElement | null} */
  _previewText = null;
  /** @type {HTMLElement | null} */
  _fontSample = null;
  /** @type {HTMLSelectElement | null} */
  _fontSelect = null;
  /** @type {HTMLElement | null} */
  _fontSelectWrap = null;
  /** @type {HTMLInputElement | null} */
  _textInput = null;
  /** @type {HTMLElement | null} */
  _colorGroup = null;
  /** @type {HTMLElement | null} */
  _selectedColorEl = null;
  /** @type {HTMLTemplateElement | null} */
  _colorTemplate = null;
  /** @type {HTMLElement | null} */
  _dialogNotice = null;
  /** @type {HTMLButtonElement | null} */
  _btnApply = null;
  /** @type {HTMLButtonElement | null} */
  _btnReset = null;

  constructor() {
    super();

    this.handleOpenClick = this.handleOpenClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);
    this.handleTextInput = this.handleTextInput.bind(this);
    this.handleTextBeforeInput = this.handleTextBeforeInput.bind(this);
    this.handleTextCompositionEnd = this.handleTextCompositionEnd.bind(this);
    this.handleApplyClick = this.handleApplyClick.bind(this);
    this.handleResetClick = this.handleResetClick.bind(this);
    this.handleColorGroupChange = this.handleColorGroupChange.bind(this);
    this.handleFontSelectChange = this.handleFontSelectChange.bind(this);
  }

  connectedCallback() {
    this._productOverview = this.closest('product-overview');
    this._config = parseConfig(this, false) || {};

    const blockWrapper = this.closest('.shopify-block--pdp-monogramming');

    const vm = this._config?.variantMap;
    if (!this._productOverview || !vm || typeof vm !== 'object' || Object.keys(vm).length === 0) {
      this.hidden = true;
      blockWrapper?.setAttribute('hidden', '');
      return;
    }

    blockWrapper?.removeAttribute('hidden');

    if (!this._uiReady) {
      this._cacheUi();
      this._bindUi();
      this._uiReady = true;
    }

    this._hydrateFromConfig();
    this._injectOrderNoticeBelowAtc();
    queueMicrotask(() => this._injectOrderNoticeBelowAtc());

    this._renderColorSwatches();
    this._renderFontControl();
    this._syncFontSample();

    const po = this._productOverview;
    this._variantUnsubscribers = [
      subscribe(EVENTS.PRODUCT_OVERVIEW_INITIALIZED, (data) => this._dispatchVariantUpdate(data)),
      subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, (data) => this._dispatchVariantUpdate(data)),
    ];

    if (po.classList.contains('product-overview--initialized')) {
      this._onVariantUpdate(po.variant);
    }
  }

  disconnectedCallback() {
    this._removeOrderNotice();

    for (const u of this._variantUnsubscribers) {
      u();
    }

    this._variantUnsubscribers = [];
    this._unbindUi();
    this._productOverview = null;
    this._uiReady = false;
  }

  _dispatchVariantUpdate(data) {
    if (data.productOverview !== this._productOverview) {
      return;
    }

    this._onVariantUpdate(data.variant);
  }

  _cacheUi() {
    const q = (sel) => this.querySelector(sel);
    this._dialog = q('[data-monogram-dialog]');
    this._btnInitial = q('[data-monogram-open="initial"]');
    this._rowAdded = q('[data-monogram-added-row]');
    this._triggerLabel = q('[data-monogram-trigger-label]');
    this._orderNoticeTemplate = q('[data-monogram-order-notice-template]');
    this._mockupWrap = q('[data-monogram-mockup-wrap]');
    this._mockupImg = q('[data-monogram-mockup-img]');
    this._previewText = q('[data-monogram-preview-text]');
    this._fontSample = q('[data-monogram-font-sample]');
    this._fontSelect = q('[data-monogram-font-select]');
    this._fontSelectWrap = q('[data-monogram-font-select-wrap]');
    this._textInput = q('[data-monogram-text-input]');
    this._colorGroup = q('[data-monogram-colors]');
    this._selectedColorEl = q('[data-monogram-selected-color]');
    this._colorTemplate = q('[data-monogram-swatch-template]');
    this._dialogNotice = q('[data-monogram-dialog-notice]');
    this._btnApply = q('[data-monogram-apply]');
    this._btnReset = q('[data-monogram-reset]');
  }

  /** @returns {HTMLElement | null} Template source node for the order notice. */
  _orderNoticeTemplateSource() {
    return this._orderNoticeTemplate?.content?.querySelector('[data-monogram-order-notice]') || null;
  }

  /** @returns {boolean} Whether the metaobject order notice has renderable content. */
  _hasOrderNoticeContent() {
    const source = this._orderNoticeTemplateSource();
    return Boolean(source?.innerHTML?.trim());
  }

  /**
   * Clones the order notice from `<template>` and renders it after the add-to-bag block.
   */
  _injectOrderNoticeBelowAtc() {
    if (!this._productOverview || !this._hasOrderNoticeContent()) {
      return;
    }

    const addToBagBlock = this._productOverview.querySelector('.shopify-block--add-to-bag');
    if (!addToBagBlock) {
      return;
    }

    if (
      this._orderNotice?.parentElement === addToBagBlock.parentElement
      && this._orderNotice.previousElementSibling === addToBagBlock
    ) {
      return;
    }

    this._removeOrderNotice();

    const source = this._orderNoticeTemplateSource();
    if (!source) {
      return;
    }

    this._orderNotice = source.cloneNode(true);
    addToBagBlock.insertAdjacentElement('afterend', this._orderNotice);
  }

  /** Removes the injected order notice clone on disconnect. */
  _removeOrderNotice() {
    this._orderNotice?.remove();
    this._orderNotice = null;
  }

  _bindUi() {
    for (const btn of this.querySelectorAll('[data-monogram-open]')) {
      btn.addEventListener('click', this.handleOpenClick);
    }

    this.querySelector('[data-monogram-close]')?.addEventListener('click', this.handleCloseClick);
    this._dialog?.addEventListener('close', this.handleDialogClose);
    this._textInput?.addEventListener('beforeinput', this.handleTextBeforeInput);
    this._textInput?.addEventListener('input', this.handleTextInput);
    this._textInput?.addEventListener('compositionend', this.handleTextCompositionEnd);
    this._btnApply?.addEventListener('click', this.handleApplyClick);
    this._btnReset?.addEventListener('click', this.handleResetClick);
    this._colorGroup?.addEventListener('change', this.handleColorGroupChange);
    this._fontSelect?.addEventListener('change', this.handleFontSelectChange);
  }

  _unbindUi() {
    for (const btn of this.querySelectorAll('[data-monogram-open]')) {
      btn.removeEventListener('click', this.handleOpenClick);
    }

    this.querySelector('[data-monogram-close]')?.removeEventListener('click', this.handleCloseClick);
    this._dialog?.removeEventListener('close', this.handleDialogClose);
    this._textInput?.removeEventListener('beforeinput', this.handleTextBeforeInput);
    this._textInput?.removeEventListener('input', this.handleTextInput);
    this._textInput?.removeEventListener('compositionend', this.handleTextCompositionEnd);
    this._btnApply?.removeEventListener('click', this.handleApplyClick);
    this._btnReset?.removeEventListener('click', this.handleResetClick);
    this._colorGroup?.removeEventListener('change', this.handleColorGroupChange);
    this._fontSelect?.removeEventListener('change', this.handleFontSelectChange);
  }

  /** @param {{ committed?: boolean }} [options] */
  _resolveSelection(variant, options = {}) {
    return resolveMonogramSelection(this._config, variant, {
      committed: options.committed ?? this._state.committed,
    });
  }

  handleOpenClick(event) {
    const variant = this._productOverview?.variant;
    if (!this._resolveSelection(variant).monogramAvailable) {
      return;
    }

    const mode = event.currentTarget.getAttribute('data-monogram-open');
    this.openDialog(mode);
  }

  handleCloseClick() {
    if (!this._dialog) {
      return;
    }

    if (typeof this._dialog.requestClose === 'function') {
      this._dialog.requestClose();
      return;
    }

    this._dialog.close();
  }

  handleDialogClose() {
    this._dialogPreviousFocus?.focus?.();
    this._dialogPreviousFocus = null;
  }

  handleTextBeforeInput(event) {
    if (event.isComposing || event.inputType === 'insertFromPaste') {
      return;
    }

    const data = event.data;
    if (data != null && MONOGRAM_TEXT_INVALID.test(data)) {
      event.preventDefault();
    }
  }

  handleTextInput() {
    this._onTextInput();
  }

  handleTextCompositionEnd() {
    this._onTextInput();
  }

  handleApplyClick() {
    this._commitFromModal();
  }

  handleResetClick() {
    this._resetMonogram();
  }

  handleColorGroupChange(event) {
    const input = event.target.closest('.pdp-monogramming__swatch-input');
    if (!input || !this._colorGroup?.contains(input)) {
      return;
    }

    this._selectColor({
      handle: input.dataset.colorHandle,
      label: input.dataset.colorLabel,
      colorFamily: input.dataset.colorFamily,
    });
  }

  handleFontSelectChange() {
    if (!this._fontSelect) {
      return;
    }

    this._selectFont(this._fontSelect.value);
  }

  _fontStyles() {
    const raw = Array.isArray(this._config.fontStyles) ? this._config.fontStyles : [];
    return raw.map((font) => String(font || '').trim()).filter(Boolean);
  }

  /** @returns {string} Sample text for font style previews (from monogram_data.font_style_preview_text). */
  _fontStylePreviewText() {
    const text = this._config.fontStylePreviewText;
    return typeof text === 'string' && text.trim() ? text.trim() : 'ABC';
  }

  _renderFontControl() {
    const fonts = this._fontStyles();
    const hasMultiple = fonts.length > 1;
    const selectedFont = this._state.draftFont || this._state.committedFont || fonts[0] || '';

    if (this._fontSample) {
      this._fontSample.hidden = hasMultiple;
    }

    if (this._fontSelectWrap) {
      this._fontSelectWrap.hidden = !hasMultiple;
    }

    if (!this._fontSelect) {
      return;
    }

    this._fontSelect.hidden = !hasMultiple;

    if (!hasMultiple) {
      this._fontSelect.innerHTML = '';
      return;
    }

    this._fontSelect.innerHTML = '';
    for (const font of fonts) {
      const option = document.createElement('option');
      option.value = font;
      option.textContent = this._fontStylePreviewText();
      option.style.fontFamily = monogramFontFamilyCss(font);
      if (font === selectedFont) {
        option.selected = true;
      }
      this._fontSelect.appendChild(option);
    }

    if (selectedFont && fonts.includes(selectedFont)) {
      this._fontSelect.value = selectedFont;
    } else if (fonts[0]) {
      this._fontSelect.value = fonts[0];
      this._state.draftFont = fonts[0];
    }
  }

  _selectFont(font) {
    const normalized = String(font || '').trim();
    if (!normalized) {
      return;
    }

    this._state.draftFont = normalized;
    this._syncFontSample();
  }

  _maxLen() {
    return Number(this._config.maxCharacterLength) || 3;
  }

  _hydrateFromConfig() {
    const orderNoticeHtml = this._orderNoticeTemplateSource()?.innerHTML?.trim() || '';
    if (this._dialogNotice) {
      this._dialogNotice.innerHTML = orderNoticeHtml;
    }

    if (this._textInput) {
      this._textInput.minLength = 1;
      this._textInput.maxLength = this._maxLen();
    }

    const fonts = this._fontStyles();
    const first = fonts[0] || '';
    if (!this._state.draftFont || !fonts.includes(this._state.draftFont)) {
      this._state.draftFont = first;
    }

    if (!this._state.committedFont || !fonts.includes(this._state.committedFont)) {
      this._state.committedFont = first;
    }

    this._syncPreviewPlacement();
  }

  _syncPreviewPlacement() {
    if (!this._previewText) {
      return;
    }

    const { x, y, angle } = normalizeMockupPlacement(this._config.mockupPlacement);
    this._previewText.style.left = `${x}%`;
    this._previewText.style.top = `${y}%`;
    this._previewText.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;
  }

  _syncMockupMedia(variant) {
    const wrap = this._mockupWrap;
    if (!wrap) {
      return;
    }

    const url = mockupMediaForVariant(this._config, variant);
    let img = this._mockupImg;

    if (!url) {
      if (img) {
        img.remove();
        this._mockupImg = null;
      }
      return;
    }

    if (!img) {
      img = document.createElement('img');
      img.className = 'pdp-monogramming__preview-img';
      img.setAttribute('data-monogram-mockup-img', '');
      img.alt = wrap.dataset.monogramMockupAlt || '';
      img.width = 300;
      img.height = 423;
      img.loading = 'lazy';
      wrap.insertBefore(img, this._previewText);
      this._mockupImg = img;
    }

    if (img.getAttribute('src') !== url) {
      img.src = url;
    }
  }

  /** @returns {string} */
  _monogramFontFamilyCss() {
    return monogramFontFamilyCss(this._state.draftFont || this._state.committedFont);
  }

  _syncFontSample() {
    const ff = this._monogramFontFamilyCss();
    if (this._fontSample) {
      this._fontSample.textContent = this._fontStylePreviewText();
      this._fontSample.style.fontFamily = ff;
    }

    if (this._fontSelect && !this._fontSelect.hidden) {
      this._fontSelect.style.fontFamily = ff;
      if (this._state.draftFont) {
        this._fontSelect.value = this._state.draftFont;
      }
    }

    if (this._textInput) {
      this._textInput.style.fontFamily = ff;
    }

    if (this._previewText) {
      this._previewText.style.fontFamily = ff;
    }
  }

  _renderColorSwatches() {
    if (!this._colorGroup || !this._colorTemplate) {
      return;
    }

    const colors = this._config.monogramColors;
    this._colorGroup.innerHTML = '';

    colors.forEach((color, index) => {
      const { handle, label, colorFamily } = color;
      const fragment = this._colorTemplate.content.cloneNode(true);
      const wrapper = fragment.querySelector('.swatch-wrapper');
      const input = fragment.querySelector('.pdp-monogramming__swatch-input');
      const swatch = fragment.querySelector('.swatch');
      if (!wrapper || !input || !swatch) {
        return;
      }

      const elementId = `${this.id || 'pdp-monogramming'}-color-${handle}`;
      input.id = elementId;
      input.name = `${this.id || 'pdp-monogramming'}-color`;
      input.value = handle;
      input.dataset.colorHandle = handle;
      input.dataset.colorLabel = label;
      input.dataset.colorFamily = colorFamily;
      input.checked = index === 0;
      input.setAttribute('aria-label', label);
      const inputLabel = wrapper.querySelector('label');
      if (inputLabel) {
        inputLabel.setAttribute('for', elementId);
      }
      swatch.classList.add(`swatch--${handle}`);

      this._colorGroup.appendChild(fragment);
    });

    if (!this._state.draftColorHandle && colors[0]) {
      this._selectColor(colors[0]);
    }

    this._syncColorSelectionUi();
    this._syncSelectedColorLabel();
  }

  _selectColor({ handle, label, colorFamily }) {
    this._state.draftColorHandle = handle;
    this._state.draftColorLabel = label;
    this._state.draftColorFamily = colorFamily;

    this._syncColorSelectionUi();
    this._syncSelectedColorLabel();
    this._refreshPreviewText();
  }

  _syncSelectedColorLabel() {
    if (!this._selectedColorEl) {
      return;
    }

    const { draftColorLabel: label, draftColorFamily: family } = this._state;

    if (family) {
      const nameText = document.createTextNode(`${label} `);
      const familySpan = document.createElement('span');
      familySpan.className = 'product__option-name__selected__color-family';
      familySpan.textContent = family;
      this._selectedColorEl.replaceChildren(nameText, familySpan);
      return;
    }

    this._selectedColorEl.textContent = label;
  }

  _syncColorSelectionUi() {
    if (!this._colorGroup) {
      return;
    }

    const selectedHandle = String(this._state.draftColorHandle || '').trim();
    for (const input of this._colorGroup.querySelectorAll('.pdp-monogramming__swatch-input')) {
      input.checked = String(input.dataset.colorHandle || '').trim() === selectedHandle;
    }
  }

  _onTextInput() {
    if (!this._textInput) {
      return;
    }

    const v = normalizeInput(this._textInput.value, this._maxLen());
    if (this._textInput.value !== v) {
      this._textInput.value = v;
    }

    this._state.draftText = v;
    this._refreshPreviewText();
    this._updateApplyEnabled();
  }

  _syncPreviewTextSizes(textLength) {
    if (!this._previewText) {
      return;
    }

    const hostStyles = getComputedStyle(this);
    const readHostVar = (name) => hostStyles.getPropertyValue(name).trim();
    const overlay = this._previewText;

    if (textLength === 3) {
      const outer = readHostVar(MONOGRAM_PREVIEW_SIZE_VARS.threeOuter);
      const inner = readHostVar(MONOGRAM_PREVIEW_SIZE_VARS.threeInner);
      if (outer) {
        overlay.style.setProperty(MONOGRAM_PREVIEW_SIZE_VARS.overlay, outer);
      }
      if (inner) {
        overlay.style.setProperty(MONOGRAM_PREVIEW_SIZE_VARS.center, inner);
      }
      return;
    }

    overlay.style.removeProperty(MONOGRAM_PREVIEW_SIZE_VARS.center);

    const size =
      textLength === 2
        ? readHostVar(MONOGRAM_PREVIEW_SIZE_VARS.two)
        : readHostVar(MONOGRAM_PREVIEW_SIZE_VARS.single);

    if (size) {
      overlay.style.setProperty(MONOGRAM_PREVIEW_SIZE_VARS.overlay, size);
    }
  }

  _refreshPreviewText() {
    if (!this._previewText) {
      return;
    }

    const text = (this._textInput?.value || '').trim() || this._state.draftText || '';
    this._previewText.innerHTML = buildMonogramPreviewHtml(text);
    this._syncPreviewTextSizes(text.length);
    const selectedSwatch = this._colorGroup?.querySelector(
      `.pdp-monogramming__swatch-input[data-color-handle="${this._state.draftColorHandle}"] + .swatch`
    );
    const selectedColor = selectedSwatch ? getComputedStyle(selectedSwatch).backgroundColor : '';
    this._previewText.style.color = selectedColor && selectedColor !== 'rgba(0, 0, 0, 0)' ? selectedColor : '#111111';
    this._syncPreviewPlacement();
  }

  _message(key, fallback) {
    const msg = this._config.messages?.[key];
    return typeof msg === 'string' && msg.trim() ? msg.trim() : fallback;
  }

  _setInitialTriggerLabel(text) {
    if (this._triggerLabel) {
      this._triggerLabel.textContent = text;
    }
  }

  _syncTriggerAvailability(variant) {
    const available = this._resolveSelection(variant).monogramAvailable;
    const addLabel = this._message('addMonogram', 'Add Monogram');
    const unavailableLabel = this._message(
      'unavailableForSelection',
      'Monogramming not available for this selection'
    );

    for (const btn of this.querySelectorAll('[data-monogram-open]')) {
      btn.disabled = !available;
      if (!available) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
    }

    if (this._btnInitial) {
      const initialLabel = available ? addLabel : unavailableLabel;
      this._btnInitial.setAttribute('aria-label', initialLabel);
      if (!this._state.committed) {
        this._setInitialTriggerLabel(initialLabel);
      }
    }
  }

  _syncTriggerLabels() {
    const committed = this._state.committed;
    this._btnInitial?.toggleAttribute('hidden', committed);
    this._rowAdded?.toggleAttribute('hidden', !committed);
  }

  _resetCommittedState() {
    this._state.committed = false;
    this._state.committedText = '';
    this._state.draftText = '';
    if (this._textInput) {
      this._textInput.value = '';
    }
  }

  _onVariantUpdate(variant) {
    const selection = this._resolveSelection(variant);
    this._state.selection = selection;

    if (this._state.committed && !selection.monogramAvailable) {
      this._resetCommittedState();
      this.closeDialog();
    }

    this._syncMonogramAddToCartData(variant);
    this._syncTriggerLabels();
    this._syncTriggerAvailability(variant);
    this._updateApplyEnabled();

    if (this._dialog?.open) {
      this._syncMockupMedia(variant);
    }
  }

  get monogramVariantId() {
    return this._state.currentMonogramVariantId;
  }

  _syncMonogramAddToCartData(variant = this._productOverview?.variant) {
    const { selection, payload } = buildMonogramAddToCartPayload(this._config, variant, this._state);

    this._state.selection = selection;
    this._state.currentMonogramVariantId = selection.monogramVariantId;
    this._state.monogramAddToCartData = payload;

    this.dataset.monogramVariantId = selection.monogramVariantId
      ? String(selection.monogramVariantId)
      : '';
    this.dataset.sendMonogramVariant = selection.sendMonogramVariant ? 'true' : 'false';
    this.dataset.monogramAddToCart = payload ? JSON.stringify(payload) : '';
  }

  get monogramAddToCartData() {
    return this._state.monogramAddToCartData;
  }

  getMonogramAddToCartData() {
    const data = this._state.monogramAddToCartData;
    if (!data) {
      return null;
    }

    const payload = { id: data.id };
    if (data.properties && typeof data.properties === 'object') {
      payload.properties = { ...data.properties };
    }
    return payload;
  }

  _updateApplyEnabled() {
    const text = (this._textInput?.value || '').trim();
    const textValid = this._textInput ? this._textInput.checkValidity() : true;
    const variantOk = this._resolveSelection(this._productOverview?.variant).monogramAvailable;
    const ok = text.length > 0 && textValid && variantOk;
    if (this._btnApply) {
      this._btnApply.disabled = !ok;
    }
  }

  openDialog(mode) {
    if (!this._dialog) {
      return;
    }

    const variant = this._productOverview?.variant;
    if (!this._resolveSelection(variant).monogramAvailable) {
      return;
    }

    this._dialogPreviousFocus = document.activeElement;

    if (mode === 'initial' || !this._state.committed) {
      this._state.draftText = '';
      this._state.draftFont = this._state.committedFont || this._fontStyles()[0] || '';
      if (this._textInput) {
        this._textInput.value = '';
      }
    } else if (this._textInput) {
      this._textInput.value = this._state.committedText;
      this._state.draftFont = this._state.committedFont;
      this._state.draftColorHandle = this._state.committedColorHandle;
      this._state.draftColorLabel = this._state.committedColorLabel;
      this._state.draftColorFamily = this._state.committedColorFamily;
      this._onTextInput();
    }

    this._renderFontControl();
    this._syncFontSample();
    this._syncColorSelectionUi();
    this._syncSelectedColorLabel();
    this._syncMockupMedia(variant);
    this._refreshPreviewText();
    this._syncTriggerLabels();
    this._syncTriggerAvailability(variant);
    this._updateApplyEnabled();
    this._dialog.showModal?.();
    this._textInput?.focus();
  }

  closeDialog() {
    if (this._dialog?.open) {
      this._dialog.close();
    }
  }

  _commitFromModal() {
    if (this._textInput && !this._textInput.checkValidity()) {
      this._textInput.reportValidity();
      return;
    }

    const text = (this._textInput?.value || '').trim().toUpperCase();
    const variant = this._productOverview?.variant;
    if (!text || !this._resolveSelection(variant).monogramAvailable) {
      return;
    }

    if (this._textInput) {
      this._textInput.value = text;
    }
    this._state.committed = true;
    this._state.committedText = text;
    this._state.committedFont = this._state.draftFont || this._state.committedFont;
    this._state.committedColorHandle = this._state.draftColorHandle;
    this._state.committedColorLabel = this._state.draftColorLabel;
    this._state.committedColorFamily = this._state.draftColorFamily;

    this._syncMonogramAddToCartData();
    this._syncTriggerLabels();
    this._syncTriggerAvailability(variant);
    this.closeDialog();
  }

  _resetMonogram() {
    this._resetCommittedState();

    const variant = this._productOverview?.variant;
    this._syncMonogramAddToCartData();
    this._refreshPreviewText();
    this._syncTriggerLabels();
    this._syncTriggerAvailability(variant);
    this._updateApplyEnabled();
  }
}

if (!customElements.get('pdp-monogramming')) {
  customElements.define('pdp-monogramming', PdpMonogramming);
}
